import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { IndexDBService } from '../index-data-base-service';
import { CLAIM_FEATURE_DB_KEY, FEATURE_DISMANTLING_DB } from '../../constants';

@injectable()
export class IndexDBServiceImpl<T> implements IndexDBService<T> {
    private _dbName: string = FEATURE_DISMANTLING_DB;
    private _storeName: string = CLAIM_FEATURE_DB_KEY;
    private async _getDB(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this._dbName, 1);

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(this._storeName)) {
                    db.createObjectStore(this._storeName);
                }
            };

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    public async saveData(key: string, value: T): Promise<void> {
        const db = await this._getDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this._storeName, 'readwrite');
            const store = transaction.objectStore(this._storeName);
            const request = store.put(value, key);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    public async getData(key: string): Promise<T> {
        const db = await this._getDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this._storeName, 'readonly');
            const store = transaction.objectStore(this._storeName);
            const request = store.get(key);

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    public async clearData(key: string): Promise<void> {
        const db = await this._getDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this._storeName, 'readwrite');
            const store = transaction.objectStore(this._storeName);
            const request = store.delete(key);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }
}
